import { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { supabase } from "../lib/supabaseClient";
import { PasswordInput } from "../components/PasswordInput";
import { PoweredBy } from "../components/PoweredBy";
import { RESET } from "../constants/testIds";

// Captured SYNCHRONOUSLY before the app bundle runs (via an inline script in
// index.html → window.__WWW_RESET_URL__), so supabase-js (detectSessionInUrl)
// cannot scrub the fragment first. Falls back to live location. Recovery links
// deliver either ?token_hash= or an implicit session fragment (#access_token=…).
const _cap = (typeof window !== "undefined" && window.__WWW_RESET_URL__) || null;
const INITIAL_HASH = _cap ? _cap.hash : (typeof window !== "undefined" ? window.location.hash : "");
const INITIAL_SEARCH = _cap ? _cap.search : (typeof window !== "undefined" ? window.location.search : "");

function readTokenHash() {
  const q = new URLSearchParams(INITIAL_SEARCH);
  let t = q.get("token_hash");
  if (!t && INITIAL_HASH) {
    const m = INITIAL_HASH.match(/token_hash=([^&]+)/);
    t = m ? decodeURIComponent(m[1]) : null;
  }
  return t;
}

function readRecoveryAccessToken() {
  const hp = new URLSearchParams(INITIAL_HASH.replace(/^#/, ""));
  if (hp.get("access_token") && hp.get("type") === "recovery") return hp.get("access_token");
  return null;
}

function scrubHash() {
  if (typeof window !== "undefined" && window.location.hash) {
    try { window.history.replaceState(null, "", window.location.pathname); } catch (_) {}
  }
}

// Guaranteed client-side sign-out: local scope clears browser storage WITHOUT a
// server round-trip (so it can't hang or 403 on an already-invalidated recovery
// token). Also drops any residual supabase auth key as belt-and-suspenders.
async function forceClientSignOut() {
  try { await supabase?.auth?.signOut({ scope: "local" }); } catch (_) {}
  try {
    Object.keys(window.localStorage).forEach((k) => {
      if (k.startsWith("sb-") && k.includes("-auth-token")) window.localStorage.removeItem(k);
    });
  } catch (_) {}
}

export default function ResetPassword() {
  const isConfirm = window.location.pathname.startsWith("/reset-password/confirm");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [token, setToken] = useState(null); // token_hash flow
  const [recoveryToken, setRecoveryToken] = useState(null); // recovery session access_token
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!isConfirm) { setChecking(false); return; }
    const th = readTokenHash();
    if (th) { setToken(th); setChecking(false); scrubHash(); return; }
    const rat = readRecoveryAccessToken();
    if (rat) { setRecoveryToken(rat); setChecking(false); scrubHash(); return; }

    // Fallback: a session supabase-js may already have created from the fragment.
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 12 && !cancelled; i++) {
        const res = await (supabase?.auth?.getSession() || Promise.resolve({ data: {} }));
        if (res?.data?.session?.access_token) {
          if (!cancelled) setRecoveryToken(res.data.session.access_token);
          break;
        }
        await new Promise((r) => setTimeout(r, 120));
      }
      scrubHash();
      if (!cancelled) setChecking(false);
    })();
    return () => { cancelled = true; };
  }, [isConfirm]);

  const request = async () => {
    if (!email) return toast.error("Enter your email");
    setBusy(true);
    await api("/auth/password/reset-request", { method: "POST", auth: false, body: { email } });
    setBusy(false);
    setDone(true); // always succeeds (no account enumeration)
  };

  const confirm = async () => {
    if (!pw || pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      let r;
      if (token) {
        r = await api("/auth/password/reset-confirm", {
          method: "POST", auth: false, body: { token_hash: token, new_password: pw },
        });
      } else if (recoveryToken) {
        // Use the recovery access token captured from the URL fragment DIRECTLY.
        // Do NOT substitute supabase.auth.getSession(): if the user already had a
        // browser session (e.g. an admin who was logged in), getSession can return
        // that non-recovery session, which the backend rightly rejects (403).
        r = await api("/auth/password/recovery-complete", {
          method: "POST", auth: false, body: { access_token: recoveryToken, new_password: pw },
        });
      } else {
        return toast.error("This reset link is invalid or has expired.");
      }
      if (!r.ok) {
        const d = r.data?.detail;
        return toast.error(
          d === "invalid_or_expired_reset_token" || d === "not_a_recovery_session"
            ? "This reset link is invalid or has expired. Please request a new link."
            : "Could not reset password. Please request a new link.");
      }
      // Password changed server-side. Force a guaranteed client-side sign-out so
      // no session (including any pre-existing one) lingers in the browser.
      await forceClientSignOut();
      setDone(true);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <div className="ww-auth">
        <div className="app" data-testid={RESET.page}>
          <header className="hdr">
            <div className="brand auth-brand" data-testid="reset-brand">
              <img src="/brand/www-logo-primary.png" className="auth-logo"
                   alt="World Wide Wallpaper — A world of joy, freely shared." />
              <span className="tag">Password reset</span>
            </div>
          </header>
          <main className="main beta-main">
            {!isConfirm && !done && (
              <div className="card auth">
                <h3><KeyRound size={16} /> Reset your password</h3>
                <p className="muted">Enter your email and we'll send a reset link.</p>
                <input data-testid={RESET.emailInput} type="email" placeholder="you@example.com"
                       value={email} onChange={(e) => setEmail(e.target.value)} />
                <button className="btn primary" data-testid={RESET.requestButton} disabled={busy} onClick={request}>
                  {busy ? "…" : "Send reset link"}
                </button>
              </div>
            )}
            {!isConfirm && done && (
              <div className="card" data-testid={RESET.requestDone}>
                <h3><CheckCircle2 size={16} className="ok" /> Check your email</h3>
                <p className="muted">If an account exists for that address, a reset link is on its way.</p>
                <a className="btn" href="/">Back to sign in</a>
              </div>
            )}
            {isConfirm && checking && !token && !recoveryToken && (
              <div className="card" data-testid="reset-checking">
                <h3><KeyRound size={16} /> Verifying your reset link…</h3>
                <p className="muted">One moment while we confirm your password reset link.</p>
              </div>
            )}
            {isConfirm && !checking && !token && !recoveryToken && (
              <div className="card" data-testid={RESET.invalid}>
                <h3><AlertTriangle size={16} className="warn" /> Invalid reset link</h3>
                <p className="muted">This link is invalid or has expired. Please request a new one.</p>
                <a className="btn" href="/reset-password">Request a new link</a>
              </div>
            )}
            {isConfirm && (token || recoveryToken) && !done && (
              <div className="card auth">
                <h3><KeyRound size={16} /> Choose a new password</h3>
                <PasswordInput testId={RESET.newPasswordInput} toggleTestId="reset-new-password-toggle"
                               placeholder="New password (min 8)" value={pw}
                               onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />
                <PasswordInput testId={RESET.newPasswordConfirmInput} toggleTestId="reset-new-password-confirm-toggle"
                               placeholder="Confirm new password" value={pw2}
                               onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" />
                <button className="btn primary" data-testid={RESET.confirmButton} disabled={busy} onClick={confirm}>
                  {busy ? "…" : "Set new password"}
                </button>
              </div>
            )}
            {isConfirm && done && (
              <div className="card" data-testid={RESET.confirmDone}>
                <h3><CheckCircle2 size={16} className="ok" /> Password reset</h3>
                <p className="muted">Your password has been reset successfully. Please sign in with your new password.</p>
                <a className="btn primary" data-testid={RESET.loginButton} href="/">Log in</a>
              </div>
            )}
          </main>
          <div className="ww-footer-standalone"><PoweredBy /></div>
        </div>
      </div>
    </>
  );
}
